import React, { useEffect, useState } from "react";

import clsx from "clsx";
import { random } from "lodash";

import metricDecreasingTrend from "@assets/metric-decreasing-trend.png";
import metricIncreasingTrend from "@assets/metric-increasing-trend.png";
import { useUser } from "@components/user-context";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { usePrevious } from "@hooks/use-previous";
import { type SalesPageMetric } from "@north-beam/nb-client-side";
import { formatting } from "@north-beam/nb-common";

import { useCurrentlyUpdatingMetric } from "../hooks/use-currently-updating-metric";
import { useFormulaBuilder } from "../hooks/use-formula-builder";

import previewData from "./preview-data.json";

interface Props {
  metrics: SalesPageMetric[];
}

const PREVIEW_ROW_COUNT = 4;

export const PreviewMetricTable = ({ metrics }: Props) => {
  const { formulaEvaluator } = useFormulaBuilder();
  const { accountingMode } = useCurrentlyUpdatingMetric();

  const [tableData, setTableData] = useState<
    Record<SalesPageMetric["id"], number>[]
  >(Array.from({ length: PREVIEW_ROW_COUNT }, () => ({})));

  const prevMetrics = usePrevious(metrics);

  useEffect(() => {
    const prevMetricsSet = new Set(
      prevMetrics?.map((metric) => metric.id) ?? [],
    );
    const isSameMetrics = metrics.every((metric) =>
      prevMetricsSet.has(metric.id),
    );

    if (!isSameMetrics) {
      setTableData((prevTableData) => {
        const tableData = [...prevTableData];

        for (let rowIndex = 0; rowIndex < PREVIEW_ROW_COUNT; rowIndex++) {
          metrics.forEach((metric) => {
            const sampleRow = previewData[accountingMode][rowIndex];
            const sampleNumber = sampleRow[metric.id as keyof typeof sampleRow];
            const prevRowData = tableData[rowIndex];

            let sensibleRandomNumber = 0;
            if (metric.format === "dollars") {
              sensibleRandomNumber = random(1_000, 10_000);
            } else if (
              metric.format === "integer" ||
              metric.format === "decimal"
            ) {
              sensibleRandomNumber = random(500, 1000);
            } else if (metric.format === "percentage") {
              sensibleRandomNumber = random(0, 1);
            } else {
              sensibleRandomNumber = random(0, 5);
            }

            prevRowData[metric.id] ||= sampleNumber || sensibleRandomNumber;
            tableData.splice(rowIndex, 1, prevRowData);
          });
        }

        return tableData;
      });
    }
  }, [metrics, prevMetrics]);

  return (
    <div className="overflow-x-scroll rounded-md">
      <table className="relative w-full h-full table-fixed">
        <thead>
          <tr>
            <TableHead
              value="Platform"
              className="min-w-[8rem] sticky left-0"
            />
            {metrics.map((metric, columnIndex) => (
              <TableHead
                key={`${columnIndex}-${metric.id}`}
                value={metric.name}
              />
            ))}
            <MetricNameTableHead />
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: PREVIEW_ROW_COUNT }).map((_, rowIndex) => (
            <tr key={rowIndex} className="border-t-gray-2 border-t">
              <PlatformTableData rowIndex={rowIndex} />
              {metrics.map((metric, columnIndex) => (
                <TableData
                  key={`${columnIndex}-${metric.id}`}
                  metric={metric}
                  cellData={tableData[rowIndex][metric.id]}
                  setCellData={(value: number) => {
                    setTableData((prevTableData) => {
                      const prevRowData = prevTableData[rowIndex];
                      prevRowData[metric.id] = value;
                      prevTableData.splice(rowIndex, 1, prevRowData);
                      return [...prevTableData];
                    });
                  }}
                />
              ))}
              <CalculatedTableData
                value={(() => {
                  try {
                    return formulaEvaluator.evaluate(tableData[rowIndex]);
                  } catch (error) {
                    return "Invalid";
                  }
                })()}
              />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MetricNameTableHead = () => {
  const { name } = useCurrentlyUpdatingMetric();
  return (
    <TableHead
      isLastColumn
      className="sticky right-0"
      value={name || "Unnamed"}
    />
  );
};

const TableHead = ({
  value,
  isLastColumn,
  className,
}: {
  value?: string;
  isLastColumn?: boolean;
  className?: string;
}) => (
  <th
    className={clsx(
      "text-xs font-semibold pl-2 py-2",
      {
        "bg-cosmic-5": isLastColumn,
        "bg-white": !isLastColumn,
      },
      className,
    )}
  >
    <div
      className={clsx("border-r border-r-gray-2 truncate pr-1", {
        "border-r-0 text-cosmic": isLastColumn,
        "border-r-1": !isLastColumn,
      })}
    >
      {value}
    </div>
  </th>
);

const TableData = ({
  metric,
  cellData,
  setCellData,
}: {
  metric: SalesPageMetric;
  cellData: any;
  setCellData: (value: number) => void;
}) => {
  const { user } = useUser();
  const [isEditable, setIsEditable] = useState(false);

  return (
    <td
      className={clsx(
        "text-sm text-right p-2 font-mono bg-white border-r",
        "border-r-gray-2 text-gray+1 min-w-[80px] cursor-text",
      )}
      onClick={() => setIsEditable(true)}
    >
      {isEditable ? (
        <input
          className={clsx(
            "w-full h-full bg-transparent outline-none [appearance:textfield] text-right",
            "[&::-webkit-outer-spin-button]:appearance-none",
            "[&::-webkit-inner-spin-button]:appearance-none",
          )}
          autoFocus
          type="number"
          value={cellData ?? ""}
          onChange={(e) => setCellData(e.target.valueAsNumber)}
          onBlur={() => setIsEditable(false)}
        />
      ) : (
        <span>
          {formatting.formatMetric.approximate(
            Number(cellData),
            metric.format,
            {
              code: user.currencies.currentCurrency,
            },
          )}
        </span>
      )}
    </td>
  );
};

const CalculatedTableData = ({ value }: { value?: string | number }) => {
  const { dataType, isNegativeTrendGood } = useCurrentlyUpdatingMetric();
  const { user } = useUser();

  const formattedValue =
    value !== "Invalid"
      ? formatting.formatMetric.approximate(Number(value), dataType, {
          code: user.currencies.currentCurrency,
        })
      : value;

  return (
    <td
      className={clsx(
        "text-sm py-2 px-1 font-mono font-semibold bg-cosmic-5 whitespace-nowrap",
        "sticky right-0 border-l border-l-gray-2 min-w-[100px]",
      )}
    >
      <div className="flex items-center justify-between space-x-2">
        <span className="w-8">
          {isNegativeTrendGood ? (
            <img src={metricDecreasingTrend} alt="Decreasing trend" />
          ) : (
            <img src={metricIncreasingTrend} alt="Increasing trend" />
          )}
        </span>
        {formattedValue}
      </div>
    </td>
  );
};

const PlatformTableData = ({ rowIndex }: { rowIndex: number }) => {
  const { designElementManager } = useDesignElementManager();
  const platform = ["Google", "Facebook Ads", "TikTok", "Snapchat"][rowIndex];
  const designElement = designElementManager.getDesignElement(platform);
  return (
    <td
      className={clsx(
        "text-xs pl-2 py-2 bg-white border-r border-r-gray-2",
        "flex items-center space-x-1 min-w-[8rem] sticky left-0",
      )}
    >
      <div>{designElement.getComponent({ size: "20px" })}</div>
      <div>{platform}</div>
    </td>
  );
};
